import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  attendanceDeleteCredential,
  attendanceLoadCredentials,
  attendanceSaveCredential,
  loadSchoolEmployees,
  type AttendanceCredentialType,
  type AttendanceEmployeeCredential,
  type AttendanceTerminal,
} from '../../lib/apiPhpAdapter'
import type { User } from '../../types'
import { formatEmployeeFullName } from '../../utils/employeeDisplay'
import { comparePersonNames } from '../../utils/personNameSort'
import './AttendanceOrgAdmin.css'

type Props = {
  busy: boolean
  setBusy: (v: boolean) => void
  showAlert: (msg: string, type?: 'success' | 'error' | 'warning' | 'info') => void
}

const TYPE_LABELS: Record<string, string> = {
  card: 'Karta',
  chip: 'Čip',
  fingerprint: 'Otisk',
}

type StatusFilter = 'all' | 'assigned' | 'unassigned'

function employeeSchoolId(e: User): number | null {
  if (e.school_employee_id != null) return Number(e.school_employee_id)
  const id = String(e.id || '')
  const m = id.match(/^se-(\d+)$/)
  if (m) return Number(m[1])
  return null
}

function credSummary(creds: AttendanceEmployeeCredential[]): string {
  if (creds.length === 0) return '—'
  return creds
    .map((c) => {
      const t = TYPE_LABELS[c.credential_type] || c.credential_type
      return `${t}: ${c.credential_value}`
    })
    .join(' · ')
}

export function AttendanceCredentialsPanel({ busy, setBusy, showAlert }: Props) {
  const [employees, setEmployees] = useState<User[]>([])
  const [credentials, setCredentials] = useState<AttendanceEmployeeCredential[]>([])
  const [terminals, setTerminals] = useState<AttendanceTerminal[]>([])
  const [types, setTypes] = useState<string[]>(['card', 'chip', 'fingerprint'])
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [loading, setLoading] = useState(true)

  const [modalEmpId, setModalEmpId] = useState<number | null>(null)
  const [credType, setCredType] = useState<AttendanceCredentialType>('card')
  const [credValue, setCredValue] = useState('')
  const [terminalId, setTerminalId] = useState('')
  const [externalId, setExternalId] = useState('')
  const [note, setNote] = useState('')
  const [editingId, setEditingId] = useState<number | null>(null)

  const reloadAll = useCallback(async () => {
    const [emps, creds] = await Promise.all([loadSchoolEmployees(), attendanceLoadCredentials({})])
    const list = Array.isArray(emps) ? [...emps] : []
    list.sort(comparePersonNames)
    setEmployees(list)
    setCredentials(Array.isArray(creds.credentials) ? creds.credentials : [])
    if (Array.isArray(creds.terminals)) setTerminals(creds.terminals)
    if (Array.isArray(creds.credential_types) && creds.credential_types.length) {
      setTypes(creds.credential_types)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    reloadAll()
      .catch((e: unknown) => {
        if (!cancelled) {
          showAlert(e instanceof Error ? e.message : 'Nepodařilo se načíst identifikátory', 'error')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [reloadAll, showAlert])

  const credsByEmployee = useMemo(() => {
    const map = new Map<number, AttendanceEmployeeCredential[]>()
    for (const c of credentials) {
      if (c.active === 0 || c.active === false) continue
      const eid = Number(c.school_employee_id)
      if (!eid) continue
      const arr = map.get(eid) || []
      arr.push(c)
      map.set(eid, arr)
    }
    return map
  }, [credentials])

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase()
    const out: Array<{
      emp: User
      sid: number
      assigned: boolean
      creds: AttendanceEmployeeCredential[]
    }> = []
    for (const e of employees) {
      const sid = employeeSchoolId(e)
      if (!sid) continue
      const creds = credsByEmployee.get(sid) || []
      const assigned = creds.length > 0
      if (statusFilter === 'assigned' && !assigned) continue
      if (statusFilter === 'unassigned' && assigned) continue
      if (q) {
        const name = formatEmployeeFullName(e).toLowerCase()
        const hay = [
          name,
          (e.email || '').toLowerCase(),
          String(e.personal_number || '').toLowerCase(),
          credSummary(creds).toLowerCase(),
        ].join(' ')
        if (!hay.includes(q)) continue
      }
      out.push({ emp: e, sid, assigned, creds })
    }
    return out
  }, [employees, credsByEmployee, search, statusFilter])

  const assignedCount = useMemo(() => {
    let n = 0
    for (const e of employees) {
      const sid = employeeSchoolId(e)
      if (sid && (credsByEmployee.get(sid) || []).length > 0) n += 1
    }
    return n
  }, [employees, credsByEmployee])

  const modalEmployee = useMemo(
    () => (modalEmpId == null ? null : employees.find((e) => employeeSchoolId(e) === modalEmpId) || null),
    [employees, modalEmpId],
  )

  const modalCreds = useMemo(() => {
    if (modalEmpId == null) return []
    return credentials.filter((c) => Number(c.school_employee_id) === modalEmpId)
  }, [credentials, modalEmpId])

  const openModal = (sid: number) => {
    setModalEmpId(sid)
    setEditingId(null)
    setCredType('card')
    setCredValue('')
    setTerminalId('')
    setExternalId('')
    setNote('')
  }

  const closeModal = () => {
    setModalEmpId(null)
    setEditingId(null)
    setCredValue('')
    setExternalId('')
    setNote('')
    setTerminalId('')
    setCredType('card')
  }

  const startEdit = (c: AttendanceEmployeeCredential) => {
    setEditingId(c.id)
    setCredType((c.credential_type as AttendanceCredentialType) || 'card')
    setCredValue(c.credential_value || '')
    setTerminalId(c.terminal_id ? String(c.terminal_id) : '')
    setExternalId(c.external_user_id || '')
    setNote(c.note || '')
  }

  const resetForm = () => {
    setEditingId(null)
    setCredValue('')
    setExternalId('')
    setNote('')
    setTerminalId('')
    setCredType('card')
  }

  const save = () => {
    void (async () => {
      if (modalEmpId == null) return
      if (!credValue.trim()) {
        showAlert('Zadejte hodnotu identifikátoru', 'info')
        return
      }
      setBusy(true)
      try {
        await attendanceSaveCredential(
          {
            school_employee_id: modalEmpId,
            credential_type: credType,
            credential_value: credValue.trim(),
            terminal_id: terminalId ? Number(terminalId) : null,
            external_user_id: externalId.trim() || null,
            note: note.trim() || null,
            active: 1,
          },
          editingId ?? undefined,
        )
        showAlert(editingId ? 'Identifikátor uložen' : 'Identifikátor přiřazen', 'success')
        resetForm()
        await reloadAll()
      } catch (e: unknown) {
        showAlert(e instanceof Error ? e.message : 'Uložení selhalo', 'error')
      } finally {
        setBusy(false)
      }
    })()
  }

  const remove = (c: AttendanceEmployeeCredential) => {
    if (
      !window.confirm(
        `Odebrat ${TYPE_LABELS[c.credential_type] || c.credential_type} „${c.credential_value}“?`,
      )
    ) {
      return
    }
    void (async () => {
      setBusy(true)
      try {
        await attendanceDeleteCredential(c.id)
        showAlert('Identifikátor odebrán', 'success')
        if (editingId === c.id) resetForm()
        await reloadAll()
      } catch (e: unknown) {
        showAlert(e instanceof Error ? e.message : 'Smazání selhalo', 'error')
      } finally {
        setBusy(false)
      }
    })()
  }

  return (
    <div className="att-cat">
      <p className="att-cat__intro">
        Přehled přiřazení čipu, karty nebo otisku. Kliknutím na řádek otevřete úpravu identifikátorů
        zaměstnance.
      </p>

      <div className="att-leave-form__filters att-cred-toolbar">
        <label className="att-leave-form__search">
          Hledat
          <input
            type="search"
            value={search}
            placeholder="jméno / os. číslo / UID"
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        <label>
          Stav
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          >
            <option value="all">Všichni</option>
            <option value="assigned">Jen přiřazení</option>
            <option value="unassigned">Jen bez identifikátoru</option>
          </select>
        </label>
        <span className="attendance-muted">
          {loading
            ? 'Načítání…'
            : `${rows.length} · přiřazeno ${assignedCount}/${employees.filter((e) => employeeSchoolId(e)).length}`}
        </span>
      </div>

      <div className="att-cat__wrap att-cat__wrap--sticky att-cred-table-wrap">
        <table className="att-cat__table att-cred-table">
          <thead>
            <tr>
              <th className="att-cat__col-name att-cat__col-fill">Zaměstnanec</th>
              <th>Os. číslo</th>
              <th>Stav</th>
              <th className="att-cat__col-fill">Identifikátory</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ emp, sid, assigned, creds }) => (
              <tr
                key={sid}
                className="att-cred-row"
                onClick={() => openModal(sid)}
                title="Otevřít nastavení identifikátorů"
              >
                <td className="att-cat__col-name att-cat__col-fill">{formatEmployeeFullName(emp)}</td>
                <td>{emp.personal_number || '—'}</td>
                <td>
                  <span
                    className={
                      assigned ? 'att-cred-badge att-cred-badge--ok' : 'att-cred-badge att-cred-badge--empty'
                    }
                  >
                    {assigned ? 'Přiřazeno' : 'Nepřiřazeno'}
                  </span>
                </td>
                <td className="att-cred-summary" title={credSummary(creds)}>
                  {credSummary(creds)}
                </td>
              </tr>
            ))}
            {!loading && rows.length === 0 ? (
              <tr>
                <td colSpan={4} className="att-cat__empty">
                  Žádný zaměstnanec neodpovídá filtru.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {modalEmpId != null && modalEmployee ? (
        <div
          className="att-cred-modal-overlay"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal()
          }}
        >
          <div
            className="att-cred-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="att-cred-modal-title"
          >
            <div className="att-cred-modal__head">
              <div>
                <h3 id="att-cred-modal-title">{formatEmployeeFullName(modalEmployee)}</h3>
                <p className="attendance-muted" style={{ margin: '4px 0 0' }}>
                  {modalEmployee.personal_number
                    ? `Os. číslo: ${modalEmployee.personal_number}`
                    : 'Bez osobního čísla'}
                  {modalEmployee.email ? ` · ${modalEmployee.email}` : ''}
                </p>
              </div>
              <button type="button" className="att-org__btn" onClick={closeModal} disabled={busy}>
                Zavřít
              </button>
            </div>

            <div className="att-cred-modal__body">
              <div className="att-org__regime-form att-cred-modal__form">
                <label>
                  Typ
                  <select
                    value={credType}
                    disabled={busy}
                    onChange={(e) => setCredType(e.target.value as AttendanceCredentialType)}
                  >
                    {types.map((t) => (
                      <option key={t} value={t}>
                        {TYPE_LABELS[t] || t}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Hodnota (UID / číslo karty / ID šablony otisku)
                  <input
                    value={credValue}
                    disabled={busy}
                    placeholder="např. 1A2B3C4D"
                    onChange={(e) => setCredValue(e.target.value)}
                  />
                </label>
                <label>
                  Terminál (volitelné — prázdné = všechny)
                  <select
                    value={terminalId}
                    disabled={busy}
                    onChange={(e) => setTerminalId(e.target.value)}
                  >
                    <option value="">Všechny terminály</option>
                    {terminals.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name} ({t.building_code})
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Externí ID ve zařízení (volitelné)
                  <input
                    value={externalId}
                    disabled={busy}
                    placeholder="vendor user id"
                    onChange={(e) => setExternalId(e.target.value)}
                  />
                </label>
                <label>
                  Poznámka
                  <input value={note} disabled={busy} onChange={(e) => setNote(e.target.value)} />
                </label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="system-button att-cat__btn"
                    disabled={busy}
                    onClick={save}
                  >
                    {editingId ? 'Uložit změnu' : 'Přidat identifikátor'}
                  </button>
                  {editingId ? (
                    <button
                      type="button"
                      className="system-button att-cat__btn"
                      disabled={busy}
                      onClick={resetForm}
                    >
                      Zrušit úpravu
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="att-cat__wrap">
                <table className="att-cat__table">
                  <thead>
                    <tr>
                      <th>Typ</th>
                      <th>Hodnota</th>
                      <th>Terminál</th>
                      <th>Ext. ID</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {modalCreds.map((c) => (
                      <tr key={c.id}>
                        <td>{TYPE_LABELS[c.credential_type] || c.credential_type}</td>
                        <td className="att-cat__mono">{c.credential_value}</td>
                        <td>{c.terminal_name || '—'}</td>
                        <td>{c.external_user_id || '—'}</td>
                        <td className="att-cat__col-actions">
                          <button
                            type="button"
                            className="system-button att-cat__btn"
                            disabled={busy}
                            onClick={() => startEdit(c)}
                          >
                            Upravit
                          </button>
                          <button
                            type="button"
                            className="system-button att-cat__btn att-cat__btn--danger"
                            disabled={busy}
                            onClick={() => remove(c)}
                          >
                            Odebrat
                          </button>
                        </td>
                      </tr>
                    ))}
                    {modalCreds.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="att-cat__empty">
                          Zatím žádný identifikátor — zaměstnanec je nepřiřazený.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
